import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  GraduationCap, Search, Clock, CheckCircle2, PlayCircle,
  Filter, Sparkles, ExternalLink,
} from "lucide-react";
import TutorialViewer from "../components/tutorials/TutorialViewer";
import { fetchTutorials } from "../tutorials/api";
import { tutorialLabel } from "../tutorials/labels";
import { useLang } from "../i18n/LangContext";

const BLOCK_FILTERS = [
  { id: "all",         key: "tut.filter.all" },
  { id: "onboarding",  key: "tut.filter.onboarding" },
  { id: "discovery",   key: "tut.filter.discovery" },
  { id: "outreach",    key: "tut.filter.outreach" },
  { id: "pipeline",    key: "tut.filter.pipeline" },
  { id: "analytics",   key: "tut.filter.analytics" },
  { id: "specialty",   key: "tut.filter.specialty" },
];

const STATE_COLORS = {
  not_started:  "bg-slate-100 text-slate-700 border-slate-200",
  in_progress:  "bg-amber-100 text-amber-800 border-amber-300",
  completed:    "bg-emerald-100 text-emerald-800 border-emerald-300",
};

const PROD_COLORS = {
  script_ready: "bg-amber-50 text-amber-700 border-amber-200",
  in_production: "bg-blue-50 text-blue-700 border-blue-200",
  review:        "bg-violet-50 text-violet-700 border-violet-200",
  published:     "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const PROD_KEY = {
  script_ready:  "tut.production.script_ready",
  in_production: "tut.production.in_production",
  review:        "tut.production.review",
  published:     "tut.production.published",
};

export default function Tutorials() {
  const { lang, t } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetchTutorials();
        if (!alive) return;
        setItems(r.items || []);
      } catch (_e) { /* silent */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  const featured = useMemo(() => items.find((i) => i.featured), [items]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items
      .filter((i) => !i.featured || filter !== "all" || q) // hide featured card from main grid when unfiltered
      .filter((i) => filter === "all" || i.block === filter)
      .filter((i) => {
        if (!q) return true;
        const label = tutorialLabel(lang, i.id);
        return `${label.title} ${label.summary} ${i.block_label || ""}`
          .toLowerCase().includes(q);
      })
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [items, filter, query, lang]);

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="tutorials-page">
      {/* Header */}
      <header className="flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
            <GraduationCap className="w-6 h-6 text-violet-600" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 dark:text-white" data-testid="tutorials-title">
              {t("tut.page.title")}
            </h1>
            <p className="text-sm text-slate-500">{t("tut.page.subtitle")}</p>
          </div>
        </div>
      </header>

      {/* Featured / Start here */}
      {featured && (
        <Card
          className="border-violet-200 bg-gradient-to-r from-violet-50 to-white dark:from-violet-900/20 dark:to-slate-900/30 cursor-pointer hover:shadow-md transition"
          data-testid="tutorial-featured-card"
          onClick={() => setOpenId(featured.id)}>
          <CardContent className="p-6 flex flex-col md:flex-row md:items-center gap-4">
            <div className="w-14 h-14 rounded-lg bg-violet-600 text-white flex items-center justify-center">
              <PlayCircle className="w-8 h-8" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Badge className="bg-violet-600 text-white text-xs">
                  <Sparkles className="w-3 h-3 mr-1" /> {t("tut.start.here")}
                </Badge>
                <Badge variant="outline" className={`text-xs ${PROD_COLORS[featured.status]}`}>
                  {t(PROD_KEY[featured.status])}
                </Badge>
              </div>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                {tutorialLabel(lang, featured.id).title}
              </h2>
              <p className="text-sm text-slate-600 dark:text-slate-300">
                {tutorialLabel(lang, featured.id).summary}
              </p>
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Clock className="w-3.5 h-3.5" />
              {t("tut.card.duration", { sec: featured.duration_seconds })}
            </div>
            <Button
              onClick={(e) => { e.stopPropagation(); setOpenId(featured.id); }}
              data-testid="tutorial-featured-open">
              {t("tut.card.watch")}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Filters + Search */}
      <div className="flex flex-col md:flex-row gap-3 md:items-center">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder={t("tut.search.placeholder")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9"
            data-testid="tutorials-search-input"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {BLOCK_FILTERS.map((b) => (
            <button
              key={b.id}
              onClick={() => setFilter(b.id)}
              data-testid={`tutorials-filter-${b.id}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                filter === b.id
                  ? "bg-violet-600 text-white border-violet-600"
                  : "bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-violet-400"
              }`}>
              {t(b.key)}
            </button>
          ))}
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="text-sm text-slate-500 py-8 text-center">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-sm text-slate-500 py-8 text-center border border-dashed rounded-lg" data-testid="tutorials-empty">
          <Filter className="w-6 h-6 mx-auto mb-2 text-slate-400" />
          No tutorials match this filter.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="tutorials-grid">
          {filtered.map((it) => {
            const lbl = tutorialLabel(lang, it.id);
            const state = it.progress?.state || "not_started";
            return (
              <Card
                key={it.id}
                className="hover:shadow-md transition cursor-pointer group"
                onClick={() => setOpenId(it.id)}
                data-testid={`tutorial-card-${it.id}`}>
                <CardContent className="p-5 flex flex-col gap-3 h-full">
                  <div className="flex items-start justify-between gap-2">
                    <Badge variant="outline" className="text-[10px] uppercase tracking-wide">
                      {it.block_label || it.block}
                    </Badge>
                    <Badge variant="outline" className={`text-[10px] ${STATE_COLORS[state]}`}>
                      {t(`tut.status.${state}`)}
                    </Badge>
                  </div>
                  <h3 className="text-base font-semibold text-slate-900 dark:text-white group-hover:text-violet-700">
                    {lbl.title}
                  </h3>
                  <p className="text-xs text-slate-500 line-clamp-2">{lbl.summary}</p>

                  {it.prerequisites?.length > 0 && (
                    <div className="text-[11px] text-slate-500">
                      <span className="font-medium">{t("tut.card.prereq")}: </span>
                      {it.prerequisites.slice(0, 2).join(" · ")}
                      {it.prerequisites.length > 2 ? " …" : ""}
                    </div>
                  )}

                  <div className="mt-auto flex items-center justify-between pt-2">
                    <div className="flex items-center gap-1.5 text-xs text-slate-500">
                      <Clock className="w-3.5 h-3.5" />
                      {t("tut.card.duration", { sec: it.duration_seconds || 60 })}
                    </div>
                    {state === "completed" && (
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    )}
                  </div>

                  <div className="flex items-center gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                    <Badge variant="outline" className={`text-[10px] ${PROD_COLORS[it.status]}`}>
                      {t(PROD_KEY[it.status])}
                    </Badge>
                    {it.route && (
                      <Link
                        to={it.route}
                        onClick={(e) => e.stopPropagation()}
                        className="ml-auto inline-flex items-center gap-1 text-xs text-violet-600 hover:text-violet-800"
                        data-testid={`tutorial-card-go-${it.id}`}>
                        <ExternalLink className="w-3 h-3" />
                        {t("tut.card.go")}
                      </Link>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <TutorialViewer
        tutorialId={openId}
        open={!!openId}
        onClose={() => setOpenId(null)}
      />
    </div>
  );
}
